/**
 * ProviderChangeCard - Rich card display for a single provider's changes.
 *
 * Provider-specific layout using shared change utilities:
 * - Header with name, type, status, change badges
 * - Key fields grid, contact section, nested arrays
 * - Amber change indicators with old-value tooltips
 * - Transfer checkboxes on changed fields + "Mark as Transferred" button
 * - UPDATED/SAME: unchanged content hidden behind "Show More" section
 */

import {
  Building2,
  Mail,
  MapPin,
  Phone,
  Printer,
  User,
} from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import { cn } from '@/lib/utils';
import type {
  ChangedProviderEntity,
  ChangedTreatmentRequestEntity,
  ContactHandleSchema,
} from '@/services/api/generated/crossMatterChangesService';

import type { CheckableFieldConfig } from '../utils/buildCheckableItems';
import { buildCheckableItems } from '../utils/buildCheckableItems';
import {
  formatDate,
  formatEnumValue,
} from '../utils/formatting';
import { useEntityChanges } from '../utils/useEntityChanges';
import { useTransferAction } from '../utils/useTransferAction';
import { useTransferChecklist } from '../utils/useTransferChecklist';
import { ChangedFieldHighlight } from './ChangedFieldHighlight';
import {
  ChangedFieldIndicator,
  NestedChangeIndicator,
} from './ChangeIndicators';
import { CollapsibleSection } from './CollapsibleSection';
import { EntityChangeCardShell } from './EntityChangeCardShell';
import { EntityField } from './EntityField';
import { EventsFallbackWithChecklist } from './EventsFallbackWithChecklist';
import type { FieldConfig } from './KeyFieldsGrid';
import { KeyFieldsGrid, KeyFieldsGridUnchanged } from './KeyFieldsGrid';
import { ShowMoreSection } from './ShowMoreSection';
import { TransferButton } from './TransferButton';
import { TransferCheckbox } from './TransferCheckbox';
import { TreatmentRequestChangeCard } from './TreatmentRequestChangeCard';

// ============================================================================
// Provider-specific helpers
// ============================================================================

function getHandleIcon(type: ContactHandleSchema['handle_type']) {
  switch (type) {
    case 'PHONE':
      return <Phone className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />;
    case 'EMAIL':
      return <Mail className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />;
    case 'FAX':
      return <Printer className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />;
    case 'MAILING_ADDRESS':
      return <MapPin className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />;
    default:
      return <Phone className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />;
  }
}

function getHandleDisplayValue(handle: ContactHandleSchema): string {
  if (handle.value) return handle.value;
  const addressParts = [
    handle.address_line1,
    handle.address_line2,
    [handle.city, handle.state, handle.zip].filter(Boolean).join(', '),
  ].filter(Boolean);
  return addressParts.join(', ') || '\u2014';
}

// Provider key fields definition
const PROVIDER_KEY_FIELDS: FieldConfig[] = [
  { key: 'treatment_status', label: 'Treatment Status', isEnum: true },
  { key: 'care_level', label: 'Care Level', isEnum: true },
  { key: 'dates_of_service', label: 'Dates of Service' },
  { key: 'treatment', label: 'Treatment' },
  { key: 'still_treating', label: 'Still Treating' },
  { key: 'treatment_status_occurred_at', label: 'Status Changed', isDate: true },
  { key: 'date_of_records_request', label: 'Records Request Date', isDate: true },
  { key: 'pain_level', label: 'Pain Level' },
  { key: 'treatment_notes', label: 'Treatment Notes' },
  { key: 'treatment_frequency', label: 'Treatment Frequency', isEnum: true },
  { key: 'treatment_progress', label: 'Treatment Progress', isEnum: true },
];

const PROVIDER_SECONDARY_FIELDS = [
  { key: 'provider_contact_ref_id', label: 'Contact Ref ID' },
  { key: 'provider_service_ref_id', label: 'Service Ref ID' },
  { key: 'inference_reasoning', label: 'AI Reasoning' },
];

// ============================================================================
// Main Component
// ============================================================================

// Checkable fields configuration for buildCheckableItems
const PROVIDER_CHECKABLE_FIELDS: CheckableFieldConfig[] = [
  { key: 'status', type: 'field' },
  { key: 'provider_type', type: 'field' },
  { key: 'description', type: 'field' },
  { key: 'treatment_status', type: 'field' },
  { key: 'care_level', type: 'field' },
  { key: 'dates_of_service', type: 'field' },
  { key: 'treatment', type: 'field' },
  { key: 'still_treating', type: 'field' },
  { key: 'treatment_status_occurred_at', type: 'field' },
  { key: 'date_of_records_request', type: 'field' },
  { key: 'pain_level', type: 'field' },
  { key: 'treatment_notes', type: 'field' },
  { key: 'treatment_frequency', type: 'field' },
  { key: 'treatment_progress', type: 'field' },
  { key: 'phone', type: 'field' },
  { key: 'email', type: 'field' },
  { key: 'fax', type: 'field' },
  { key: 'address', type: 'field' },
  { key: 'practitioner_names', type: 'nested' },
  { key: 'handles', type: 'array' },
  { key: 'encounters', type: 'array' },
  { key: 'fallback_leads', type: 'array' },
  { key: 'provider_contact_ref_id', type: 'field' },
  { key: 'provider_service_ref_id', type: 'field' },
  { key: 'inference_reasoning', type: 'field' },
];

export const ProviderChangeCard: React.FC<{
  entity: ChangedProviderEntity;
  treatmentRequests?: ChangedTreatmentRequestEntity[];
}> = ({ entity, treatmentRequests = [] }) => {
  const displayData =
    entity.change_type === 'DELETED'
      ? (entity.old ?? entity.current)
      : (entity.current ?? entity.old);

  const changes = useEntityChanges(
    entity.change_type,
    entity.old,
    entity.current,
  );

  // Compute checkable items using the shared utility
  const entityData = (entity.current ?? entity.old) as Record<string, unknown> | undefined;
  const checkableItems = React.useMemo(
    () => buildCheckableItems(
      entity.change_type,
      entityData,
      entity.events,
      changes,
      PROVIDER_CHECKABLE_FIELDS,
    ),
    [entity.change_type, entityData, entity.events, changes],
  );

  const checklist = useTransferChecklist(entity.change_type, checkableItems);

  // Track nested treatment request readiness
  const [childReady, setChildReady] = React.useState<Record<string, boolean>>(
    {},
  );
  const handleChildReady = React.useCallback(
    (entityId: string, ready: boolean) => {
      setChildReady((prev) => ({ ...prev, [entityId]: ready }));
    },
    [],
  );

  const hasTreatmentRequests = treatmentRequests.length > 0;
  const hasTreatmentRequestChanges = treatmentRequests.some(
    (tr) => tr.change_type !== 'SAME',
  );

  // Transfer button: own items + all nested treatment request items must be ready
  const allChildrenReady = treatmentRequests.every(
    (tr) => childReady[tr.entity_id] !== false,
  );
  const transferButtonEnabled = checklist.buttonEnabled && allChildrenReady;
  // Show button if provider needs it OR if there are treatment requests with changes
  const showTransferButton =
    checklist.showButton || hasTreatmentRequestChanges;

  // Collect event IDs for transfer: own events + all nested treatment request events
  const transferEventIds = React.useMemo(
    () => [
      ...entity.events.map((e) => e.id),
      ...treatmentRequests.flatMap((tr) => tr.events.map((e) => e.id)),
    ],
    [entity.events, treatmentRequests],
  );
  const transfer = useTransferAction(transferEventIds);

  if (!displayData) return null;

  const data = displayData as unknown as Record<string, unknown>;

  // Contact fields (flat)
  const contactFields = [
    { key: 'phone', label: 'Phone', icon: Phone, value: displayData.phone },
    { key: 'email', label: 'Email', icon: Mail, value: displayData.email },
    { key: 'fax', label: 'Fax', icon: Printer, value: displayData.fax },
    {
      key: 'address',
      label: 'Address',
      icon: MapPin,
      value: displayData.address,
    },
  ];
  const visibleContacts = contactFields.filter(
    (f) => f.value || changes.isFieldChanged(f.key),
  );
  const changedContacts = visibleContacts.filter((f) =>
    changes.isFieldChanged(f.key),
  );
  const unchangedContacts = visibleContacts.filter(
    (f) => f.value && !changes.isFieldChanged(f.key),
  );

  // Nested data
  const handles = displayData.handles ?? [];
  const handlesChanged = changes.hasChangesUnder('handles');

  const encounters = displayData.encounters ?? [];
  const encountersChanged = changes.hasChangesUnder('encounters');

  const fallbackLeads = displayData.fallback_leads ?? [];
  const fallbackLeadsChanged = changes.hasChangesUnder('fallback_leads');

  const practitionerNames = displayData.practitioner_names ?? [];
  const practitionerNamesChanged =
    changes.isFieldChanged('practitioner_names') ||
    changes.hasChangesUnder('practitioner_names');

  // Description
  const { description } = displayData;
  const descriptionChanged = changes.isFieldChanged('description');

  // Secondary fields
  const visibleSecondaryFields = PROVIDER_SECONDARY_FIELDS.filter(
    (f) => data[f.key] || changes.isFieldChanged(f.key),
  );
  const changedSecondaryFields = visibleSecondaryFields.filter((f) =>
    changes.isFieldChanged(f.key),
  );
  const unchangedSecondaryFields = visibleSecondaryFields.filter(
    (f) => data[f.key] && !changes.isFieldChanged(f.key),
  );

  // Events fallback
  const isUpdatedWithoutDiffs =
    entity.change_type === 'UPDATED' && changes.totalChanges === 0;

  const hasExpandedContent =
    changes.totalChanges > 0 ||
    descriptionChanged ||
    visibleContacts.length > 0 ||
    handles.length > 0 ||
    practitionerNames.length > 0 ||
    practitionerNamesChanged ||
    encounters.length > 0 ||
    encountersChanged ||
    fallbackLeads.length > 0 ||
    fallbackLeadsChanged ||
    visibleSecondaryFields.length > 0 ||
    isUpdatedWithoutDiffs ||
    hasTreatmentRequests;

  // Determine if there's any unchanged content to show
  const isUpdatedOrSame =
    entity.change_type === 'UPDATED' || entity.change_type === 'SAME';
  const hasUnchangedContent =
    isUpdatedOrSame &&
    (PROVIDER_KEY_FIELDS.some((f) => {
      const val = data[f.key];
      const hasValue = val !== null && val !== undefined && val !== '';
      return hasValue && !changes.isFieldChanged(f.key);
    }) ||
      unchangedContacts.length > 0 ||
      (!handlesChanged && handles.length > 0) ||
      (!practitionerNamesChanged && practitionerNames.length > 0) ||
      (!encountersChanged && encounters.length > 0) ||
      (!fallbackLeadsChanged && fallbackLeads.length > 0) ||
      unchangedSecondaryFields.length > 0);

  return (
    <EntityChangeCardShell
      changeType={entity.change_type}
      name={entity.name}
      description={entity.description}
      icon={Building2}
      iconBgClassName="bg-blue-50"
      iconTextClassName="text-blue-600"
      badges={
        displayData.provider_type && (
          <Badge
            color={
              displayData.provider_type === 'ORGANIZATION' ? 'blue' : 'zinc'
            }
            className="text-[10px] shrink-0"
          >
            {formatEnumValue(displayData.provider_type)}
          </Badge>
        )
      }
      changeCount={changes.totalChanges}
      eventCount={entity.events.length}
      checklist={checklist}
      hasExpandedContent={hasExpandedContent}
    >
      {/* Events fallback */}
      {isUpdatedWithoutDiffs && (
        <EventsFallbackWithChecklist
          events={entity.events}
          checklist={checklist}
        />
      )}

      {/* Key fields grid - only changed for UPDATED/SAME */}
      <KeyFieldsGrid
        fields={PROVIDER_KEY_FIELDS}
        data={data}
        changes={changes}
        checklist={checklist}
        changeType={entity.change_type}
      />

      {/* Description — only show when changed */}
      {descriptionChanged && (
        <ChangedFieldHighlight
          label="Description"
          fieldKey="description"
          changes={changes}
          checklist={checklist}
        >
          <p className="text-sm text-gray-900 whitespace-pre-wrap">
            {description || '\u2014'}
          </p>
        </ChangedFieldHighlight>
      )}

      {/* Contact section - changed contacts */}
      {changedContacts.length > 0 && (
        <div className="space-y-0.5">
          {changedContacts.map((f) => {
            const isChanged = changes.isFieldChanged(f.key);
            const Icon = f.icon;
            return (
              <div
                key={f.key}
                className={cn(
                  'flex items-center gap-2 text-sm',
                  isChanged
                    ? 'rounded-md px-3 py-1.5 bg-amber-50/60'
                    : 'px-1 py-0.5',
                )}
              >
                <Icon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                <span
                  className={cn(
                    isChanged ? 'text-gray-900' : 'text-gray-700',
                    'flex-1',
                  )}
                >
                  {f.value || '\u2014'}
                </span>
                {isChanged && (
                  <ChangedFieldIndicator
                    oldValue={changes.getOldValue(f.key)}
                    label={f.label}
                  />
                )}
                {checklist.showCheckboxes && isChanged && (
                  <TransferCheckbox
                    checked={checklist.isChecked(f.key)}
                    onToggle={() => checklist.toggle(f.key)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Handles (additional contacts) - changed */}
      {handlesChanged && handles.length > 0 && (
        <CollapsibleSection
          title="Additional Contacts"
          count={handles.length}
          hasChanges={handlesChanged}
          changeType={entity.change_type}
        >
          {handles.map((handle, idx) => {
            const handleChanged = changes.hasChangesUnder(
              `handles.${idx}`,
            );
            // Only show changed handles in this section
            if (!handleChanged) return null;
            return (
              <div
                key={handle.id ?? idx}
                className={cn(
                  'flex items-start gap-2 text-sm',
                  handleChanged
                    ? 'rounded-md px-3 py-1.5 bg-amber-50/60'
                    : 'px-1 py-0.5',
                )}
              >
                {getHandleIcon(handle.handle_type)}
                <div className="flex-1 min-w-0">
                  <span className="text-gray-900">
                    {getHandleDisplayValue(handle)}
                  </span>
                  {handle.name && (
                    <span className="text-xs text-gray-500 ml-1.5">
                      ({handle.name})
                    </span>
                  )}
                  {handle.preferred && (
                    <Badge color="blue" className="text-[10px] ml-1.5">
                      Preferred
                    </Badge>
                  )}
                </div>
                <NestedChangeIndicator
                  prefix={`handles.${idx}`}
                  changes={changes}
                />
                {checklist.showCheckboxes && handleChanged && (
                  <TransferCheckbox
                    checked={checklist.isChecked(`handles.${idx}`)}
                    onToggle={() => checklist.toggle(`handles.${idx}`)}
                  />
                )}
              </div>
            );
          })}
        </CollapsibleSection>
      )}

      {/* Practitioner names - changed */}
      {practitionerNamesChanged && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs text-gray-400 shrink-0">
            Practitioners:
          </span>
          {practitionerNamesChanged && (
            <ChangedFieldIndicator
              oldValue={changes.getOldValue('practitioner_names')}
              label="Practitioners"
            />
          )}
          {practitionerNames.length > 0 ? (
            practitionerNames.map((name, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
              >
                <User className="h-3 w-3 text-gray-400" />
                {name}
              </span>
            ))
          ) : (
            <span className="text-sm text-gray-400">{'\u2014'}</span>
          )}
          {checklist.showCheckboxes && practitionerNamesChanged && (
            <TransferCheckbox
              checked={checklist.isChecked('practitioner_names')}
              onToggle={() => checklist.toggle('practitioner_names')}
              className="ml-auto"
            />
          )}
        </div>
      )}

      {/* Encounters - changed */}
      {encountersChanged && encounters.length > 0 && (
        <CollapsibleSection
          title="Encounters"
          count={encounters.length}
          hasChanges={encountersChanged}
          changeType={entity.change_type}
        >
          {encounters.map((encounter, idx) => {
            const encChanged = changes.hasChangesUnder(
              `encounters.${idx}`,
            );
            // Only show changed encounters in this section
            if (!encChanged) return null;
            const dateChanged = changes.isFieldChanged(
              `encounters.${idx}.date`,
            );
            const descChanged = changes.isFieldChanged(
              `encounters.${idx}.description`,
            );
            const servicesChanged = changes.hasChangesUnder(
              `encounters.${idx}.services`,
            );

            return (
              <div
                key={encounter.id ?? idx}
                className={cn(
                  'flex items-start gap-2',
                )}
              >
                <div
                  className={cn(
                    'flex-1 rounded-md border px-3 py-2 text-sm',
                    encChanged
                      ? 'border-amber-200 bg-amber-50/40'
                      : 'border-gray-100 bg-gray-50',
                  )}
                >
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {encounter.date && (
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        {formatDate(encounter.date)}
                        {dateChanged && (
                          <ChangedFieldIndicator
                            oldValue={changes.getOldValue(
                              `encounters.${idx}.date`,
                            )}
                            label="Date"
                          />
                        )}
                      </span>
                    )}
                    {encounter.services?.map((svc, i) => (
                      <Badge key={i} color="zinc" className="text-[10px]">
                        {svc}
                      </Badge>
                    ))}
                    {servicesChanged && (
                      <ChangedFieldIndicator
                        oldValue={changes.getOldValue(
                          `encounters.${idx}.services`,
                        )}
                        label="Services"
                      />
                    )}
                  </div>
                  <div className="flex items-start gap-1">
                    <p className="text-gray-700 flex-1">
                      {encounter.description}
                    </p>
                    {descChanged && (
                      <ChangedFieldIndicator
                        oldValue={changes.getOldValue(
                          `encounters.${idx}.description`,
                        )}
                        label="Description"
                      />
                    )}
                  </div>
                </div>
                {checklist.showCheckboxes && encChanged && (
                  <TransferCheckbox
                    checked={checklist.isChecked(`encounters.${idx}`)}
                    onToggle={() => checklist.toggle(`encounters.${idx}`)}
                    className="mt-2.5"
                  />
                )}
              </div>
            );
          })}
        </CollapsibleSection>
      )}

      {/* Fallback leads - changed only */}
      {fallbackLeadsChanged && fallbackLeads.length > 0 && (
        <CollapsibleSection
          title="Alternative Contacts"
          count={fallbackLeads.length}
          hasChanges={fallbackLeadsChanged}
          changeType={entity.change_type}
        >
          {fallbackLeads.map((lead, idx) => {
            const leadChanged = changes.hasChangesUnder(
              `fallback_leads.${idx}`,
            );
            return (
              <div key={idx} className="flex items-start gap-2">
                <div
                  className={cn(
                    'flex-1 rounded-md border px-3 py-2 text-sm',
                    leadChanged
                      ? 'border-amber-200 bg-amber-50/40'
                      : 'border-gray-100 bg-gray-50',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      {lead.organization_name && (
                        <span className="font-medium text-gray-900">
                          {lead.organization_name}
                        </span>
                      )}
                      {lead.practitioner_name && (
                        <span className="text-gray-700 ml-1">
                          {lead.practitioner_name}
                        </span>
                      )}
                    </div>
                    <NestedChangeIndicator
                      prefix={`fallback_leads.${idx}`}
                      changes={changes}
                    />
                  </div>
                  {lead.address && (
                    <p className="text-xs text-gray-500 mt-1">
                      {lead.address}
                    </p>
                  )}
                  {lead.internal_note && (
                    <p className="text-xs text-gray-500 mt-1 italic">
                      {lead.internal_note}
                    </p>
                  )}
                </div>
                {checklist.showCheckboxes && leadChanged && (
                  <TransferCheckbox
                    checked={checklist.isChecked(`fallback_leads.${idx}`)}
                    onToggle={() =>
                      checklist.toggle(`fallback_leads.${idx}`)
                    }
                    className="mt-2.5"
                  />
                )}
              </div>
            );
          })}
        </CollapsibleSection>
      )}

      {/* Secondary / Reference fields - changed */}
      {changedSecondaryFields.length > 0 && (
        <CollapsibleSection
          title="References"
          count={visibleSecondaryFields.length}
          hasChanges={changedSecondaryFields.length > 0}
          changeType={entity.change_type}
        >
          {changedSecondaryFields.map((f) => {
            const isChanged = changes.isFieldChanged(f.key);
            return (
              <div
                key={f.key}
                className={cn(
                  'flex items-start',
                  checklist.showCheckboxes && isChanged && 'gap-2',
                )}
              >
                <div className="flex-1 min-w-0">
                  <EntityField
                    label={f.label}
                    value={data[f.key]}
                    fieldKey={f.key}
                    changes={changes}
                  />
                </div>
                {checklist.showCheckboxes && isChanged && (
                  <TransferCheckbox
                    checked={checklist.isChecked(f.key)}
                    onToggle={() => checklist.toggle(f.key)}
                    className="mt-2.5"
                  />
                )}
              </div>
            );
          })}
        </CollapsibleSection>
      )}

      {/* Nested treatment requests */}
      {hasTreatmentRequests && (
        <CollapsibleSection
          title="Treatment Requests"
          count={treatmentRequests.length}
          hasChanges={hasTreatmentRequestChanges}
          changeType={entity.change_type}
        >
          {treatmentRequests.map((tr) => (
            <TreatmentRequestChangeCard
              key={tr.entity_id}
              entity={tr}
              nested
              onTransferReady={handleChildReady}
            />
          ))}
        </CollapsibleSection>
      )}

      {/* Show More Section - unchanged content for UPDATED/SAME */}
      {hasUnchangedContent && (
        <ShowMoreSection changeType={entity.change_type}>
          {/* Unchanged key fields */}
          <KeyFieldsGridUnchanged
            fields={PROVIDER_KEY_FIELDS}
            data={data}
            changes={changes}
            checklist={checklist}
          />

          {/* Unchanged contact fields */}
          {unchangedContacts.length > 0 && (
            <div className="space-y-0.5">
              {unchangedContacts.map((f) => {
                const Icon = f.icon;
                return (
                  <div
                    key={f.key}
                    className="flex items-center gap-2 text-sm px-1 py-0.5"
                  >
                    <Icon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    <span className="text-gray-700 flex-1">
                      {f.value || '\u2014'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Unchanged handles */}
          {!handlesChanged && handles.length > 0 && (
            <CollapsibleSection
              title="Additional Contacts"
              count={handles.length}
              hasChanges={false}
              changeType={entity.change_type}
            >
              {handles.map((handle, idx) => (
                <div
                  key={handle.id ?? idx}
                  className="flex items-start gap-2 text-sm px-1 py-0.5"
                >
                  {getHandleIcon(handle.handle_type)}
                  <div className="flex-1 min-w-0">
                    <span className="text-gray-900">
                      {getHandleDisplayValue(handle)}
                    </span>
                    {handle.name && (
                      <span className="text-xs text-gray-500 ml-1.5">
                        ({handle.name})
                      </span>
                    )}
                    {handle.preferred && (
                      <Badge color="blue" className="text-[10px] ml-1.5">
                        Preferred
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </CollapsibleSection>
          )}

          {/* Unchanged practitioners */}
          {!practitionerNamesChanged && practitionerNames.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs text-gray-400 shrink-0">
                Practitioners:
              </span>
              {practitionerNames.map((name, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
                >
                  <User className="h-3 w-3 text-gray-400" />
                  {name}
                </span>
              ))}
            </div>
          )}

          {/* Unchanged encounters */}
          {!encountersChanged && encounters.length > 0 && (
            <CollapsibleSection
              title="Encounters"
              count={encounters.length}
              hasChanges={false}
              changeType={entity.change_type}
            >
              {encounters.map((encounter, idx) => (
                <div
                  key={encounter.id ?? idx}
                  className="flex items-start gap-2"
                >
                  <div className="flex-1 rounded-md border px-3 py-2 text-sm border-gray-100 bg-gray-50">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {encounter.date && (
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          {formatDate(encounter.date)}
                        </span>
                      )}
                      {encounter.services?.map((svc, i) => (
                        <Badge key={i} color="zinc" className="text-[10px]">
                          {svc}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-gray-700">
                      {encounter.description}
                    </p>
                  </div>
                </div>
              ))}
            </CollapsibleSection>
          )}

          {/* Unchanged fallback leads */}
          {!fallbackLeadsChanged && fallbackLeads.length > 0 && (
            <CollapsibleSection
              title="Alternative Contacts"
              count={fallbackLeads.length}
              hasChanges={false}
              changeType={entity.change_type}
            >
              {fallbackLeads.map((lead, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <div className="flex-1 rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        {lead.organization_name && (
                          <span className="font-medium text-gray-900">
                            {lead.organization_name}
                          </span>
                        )}
                        {lead.practitioner_name && (
                          <span className="text-gray-700 ml-1">
                            {lead.practitioner_name}
                          </span>
                        )}
                      </div>
                    </div>
                    {lead.address && (
                      <p className="text-xs text-gray-500 mt-1">
                        {lead.address}
                      </p>
                    )}
                    {lead.internal_note && (
                      <p className="text-xs text-gray-500 mt-1 italic">
                        {lead.internal_note}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </CollapsibleSection>
          )}

          {/* Unchanged secondary fields */}
          {unchangedSecondaryFields.length > 0 && (
            <CollapsibleSection
              title="References"
              count={visibleSecondaryFields.length}
              hasChanges={false}
              changeType={entity.change_type}
            >
              {unchangedSecondaryFields.map((f) => (
                <div key={f.key} className="flex items-start">
                  <div className="flex-1 min-w-0">
                    <EntityField
                      label={f.label}
                      value={data[f.key]}
                      fieldKey={f.key}
                      changes={changes}
                    />
                  </div>
                </div>
              ))}
            </CollapsibleSection>
          )}
        </ShowMoreSection>
      )}

      {/* Transfer button */}
      {showTransferButton && (
        <TransferButton
          enabled={transferButtonEnabled}
          onClick={transfer.handleTransfer}
          onSkip={transfer.handleSkip}
          isLoading={transfer.isLoading}
          status={transfer.status}
        />
      )}
    </EntityChangeCardShell>
  );
};
